import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { History, ChevronUp, ChevronDown, ExternalLink, RefreshCw, CalendarClock, Edit2, AlertTriangle, ShieldAlert, Smartphone, CheckCircle2 } from 'lucide-react';
import { FormattedText } from '../../lib/formatContent';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { PostStatus } from './components/PostStatus';
import { PostMetadata } from './components/PostMetadata';
import { PostImage } from './components/PostImage';
import { PostHistory } from './components/PostHistory';
import { PlatformIcon } from './components/PlatformIcon';
import { manualPublishPost } from '../../lib/facebookPublisher';
import { EmojiPicker } from './EmojiPicker';

interface PostPreviewProps {
  post: any;
  task: any;
  onPostUpdate?: () => void;
}

export const PostPreview: React.FC<PostPreviewProps> = ({ post, task, onPostUpdate }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [testHistory, setTestHistory] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishProgress, setPublishProgress] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [readyPost, setReadyPost] = useState<any>(null);
  const [isLoadingReadyPost, setIsLoadingReadyPost] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const imageUrl = post?.image_url || (task?.modify_image ? null : task?.image_url);

  // Check if post is stuck (in pending, in progress, or is an auto test)
  const isStuck = post && (
    post.status === 'pending' || 
    post.status === 'In Progress' ||
    post.metadata?.is_auto_test || 
    post.metadata?.is_in_progress
  );

  // Check if post is stuck in processing for more than 5 minutes
  const isStuckInProcessing = post && 
    post.status === 'pending' && 
    post.metadata?.is_in_progress === true &&
    post.metadata?.stage === 'creation' &&
    post.metadata?.creation_timestamp &&
    (new Date().getTime() - new Date(post.metadata.creation_timestamp).getTime() > 5 * 60 * 1000);

  useEffect(() => {
    if (showHistory && task?.id) {
      loadTestHistory();
    }
  }, [showHistory, task?.id]);

  useEffect(() => {
    if (task?.id) {
      loadReadyPost();
    }
  }, [task?.id, post]);

  // Auto-reset stuck posts
  useEffect(() => {
    if (isStuckInProcessing && task?.id && !isResetting) {
      console.log('Post is stuck in processing for more than 5 minutes, auto-resetting...');
      handleResetPost();
    }
  }, [post, task]);

  const loadReadyPost = async () => {
    if (!task?.id) return;

    setIsLoadingReadyPost(true);
    try {
      // IMPORTANT: For recurring tasks, only show ready_post if:
      // 1. It's for the current scheduled date (task.scheduled_for matches today or is in the past)
      // 2. OR there's a published post for this specific occurrence

      const taskDate = new Date(task.scheduled_for);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      taskDate.setHours(0, 0, 0, 0);

      // If task is in the future and no published post exists, don't show ready_post
      if (taskDate > today) {
        const hasPublishedPost = task.posts?.some(p =>
          p.status === 'published' || p.platform_post_id || p.platform_post_url
        );

        if (!hasPublishedPost) {
          setReadyPost(null);
          setIsLoadingReadyPost(false);
          return;
        }
      }

      // For recurring tasks, filter by execution_id to get the correct occurrence's ready post
      const currentExecutionId = post?.execution_id;
      
      let readyPostQuery = supabase
        .from('ready_posts')
        .select('*')
        .eq('task_id', task.id)
        .eq('is_default', true);
      
      // If we have execution_id, filter by it for precise matching
      if (currentExecutionId) {
        readyPostQuery = readyPostQuery.eq('execution_id', currentExecutionId);
      }
      
      const { data: defaultReadyPost, error: readyError } = await readyPostQuery
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (readyError) {
        throw readyError;
      }

      if (defaultReadyPost) {
        // For recurring tasks, only show ready_post if it was created on the same day as scheduled_for
        // This prevents showing today's ready_post for future occurrences
        const readyPostDate = new Date(defaultReadyPost.created_at);
        readyPostDate.setHours(0, 0, 0, 0);

        // Only show ready_post if created on the same day as this task occurrence
        if (readyPostDate.getTime() === taskDate.getTime()) {
          setReadyPost({...defaultReadyPost, table: 'ready_posts'});
          return;
        }

        // Ready post exists but for a different day - don't show it
        setReadyPost(null);
        setIsLoadingReadyPost(false);
        return;
      }

      // If no default ready post, get the latest ready post from posts table
      const { data: readyPostFromPosts, error: postsError } = await supabase
        .from('posts')
        .select('*')
        .eq('task_id', task.id)
        .eq('status', 'ready')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (postsError) {
        throw postsError;
      }

      if (readyPostFromPosts) {
        setReadyPost(readyPostFromPosts);
      } else {
        setReadyPost(null);
      }
    } catch (error) {
      console.error('Error loading ready post:', error);
      toast.error('Failed to load ready post');
    } finally {
      setIsLoadingReadyPost(false);
    }
  };

  const loadTestHistory = async () => {
    if (!task?.id) return;

    setIsLoadingHistory(true);
    try {
      // Two steps: pick this occurrence's rows from light columns, then read only
      // those in full. Reading content + metadata for a task's whole history
      // (every day of a daily task) ran into the statement timeout.
      const POST_HISTORY_COLUMNS = 'id, content, image_url, status, created_at, metadata, platform_post_id, platform_post_url, platform_post_created_at, execution_id';
      const READY_HISTORY_COLUMNS = 'id, content, image_url, created_at, metadata, is_default, execution_id, publication_status';

      const [postIndex, readyIndex] = await Promise.all([
        supabase.from('posts').select('id, created_at, execution_id').eq('task_id', task.id),
        supabase.from('ready_posts').select('id, created_at, execution_id').eq('task_id', task.id)
      ]);

      if (postIndex.error) throw postIndex.error;
      if (readyIndex.error) throw readyIndex.error;

      // For recurring tasks, filter BOTH posts and ready_posts by execution_id or date
      // This ensures we only show posts from the current execution
      const currentExecutionId = post?.execution_id;

      const belongsToOccurrence = (row: { created_at: string; execution_id: string | null }) => {
        // If we have execution_id, use it for precise matching
        if (currentExecutionId && row.execution_id) {
          return row.execution_id === currentExecutionId;
        }

        // Fallback: filter by date for old posts without execution_id
        const taskDate = new Date(task.scheduled_for);
        taskDate.setHours(0, 0, 0, 0);
        const postDate = new Date(row.created_at);
        postDate.setHours(0, 0, 0, 0);
        return postDate.getTime() === taskDate.getTime();
      };

      const postIds = (postIndex.data || []).filter(belongsToOccurrence).map(row => row.id);
      const readyIds = (readyIndex.data || []).filter(belongsToOccurrence).map(row => row.id);

      const [postsResult, readyPostsResult] = await Promise.all([
        postIds.length
          ? supabase.from('posts').select(POST_HISTORY_COLUMNS).in('id', postIds)
          : Promise.resolve({ data: [], error: null }),
        readyIds.length
          ? supabase.from('ready_posts').select(READY_HISTORY_COLUMNS).in('id', readyIds)
          : Promise.resolve({ data: [], error: null })
      ]);

      if (postsResult.error) throw postsResult.error;
      if (readyPostsResult.error) throw readyPostsResult.error;

      const filteredPosts: any[] = postsResult.data || [];
      const filteredReadyPosts: any[] = readyPostsResult.data || [];

      // Combine and sort posts
      const allPosts = [
        ...filteredPosts,
        ...filteredReadyPosts.map(post => ({...post, table: 'ready_posts'}))
      ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      
      setTestHistory(allPosts);
    } catch (error) {
      console.error('Error loading test history:', error);
      toast.error('Failed to load test history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleSetAsDefault = async (selectedPost: any) => {
    if (!task?.id || !selectedPost?.id) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      if (selectedPost.table === 'ready_posts') {
        // If the post is already from ready_posts, just update is_default
        await supabase
          .from('ready_posts')
          .update({ is_default: true })
          .eq('id', selectedPost.id);
          
        // Mark other ready posts as not default
        await supabase
          .from('ready_posts')
          .update({ is_default: false })
          .eq('task_id', task.id)
          .neq('id', selectedPost.id);
      } else {
        // First mark all other posts as skipped
        await supabase
          .from('posts')
          .update({
            status: 'skipped',
            metadata: {
              ...selectedPost.metadata,
              is_in_progress: false,
              stage: 'skipped',
              skip_reason: 'Another post was set as default',
              skip_timestamp: new Date().toISOString()
            }
          })
          .eq('task_id', task.id)
          .neq('id', selectedPost.id)
          .neq('status', 'published');

        // Then set the selected post as ready
        const { error } = await supabase
          .from('posts')
          .update({
            status: 'ready',
            metadata: {
              ...selectedPost.metadata,
              is_in_progress: false,
              stage: 'ready',
              ready_timestamp: new Date().toISOString()
            }
          })
          .eq('id', selectedPost.id);

        if (error) throw error;

        // Create or update ready post
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('Not authenticated');
        }

        const { error: readyPostError } = await supabase.rpc('mark_post_as_ready', {
          p_post_id: selectedPost.id,
          p_user_id: user.id,
          p_content: selectedPost.content,
          p_image_url: selectedPost.image_url
        });

        if (readyPostError) throw readyPostError;
      }

      await loadTestHistory();
      await loadReadyPost();
      if (onPostUpdate) onPostUpdate();
      toast.success('Post has been set as default');
    } catch (error) {
      console.error('Error setting post as default:', error);
      toast.error('Failed to set post as default');
    }
  };

  const handleResetPost = async () => {
    if (!task?.id || isResetting) return;

    setIsResetting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      const isTokenError = post?.metadata?.error?.toLowerCase().includes('token') ||
        post?.metadata?.error?.toLowerCase().includes('expired') ||
        post?.metadata?.error?.toLowerCase().includes('oauth') ||
        post?.metadata?.error_response?.includes('EXPIRED_ACCESS_TOKEN');

      if (post?.id && (post.status === 'error' || post.status === 'pending') && isTokenError) {
        const cleanMetadata = { ...post.metadata };
        delete cleanMetadata.error;
        delete cleanMetadata.error_details;
        delete cleanMetadata.error_response;
        delete cleanMetadata.error_status;
        delete cleanMetadata.error_at;

        await supabase
          .from('posts')
          .update({
            status: 'ready',
            publication_status: 'pending',
            publication_attempt_count: 0,
            metadata: {
              ...cleanMetadata,
              is_in_progress: false,
              stage: 'ready',
              retry_after_token_refresh: true,
              retry_timestamp: new Date().toISOString()
            }
          })
          .eq('id', post.id);

        if (post.metadata?.ready_post_id) {
          await supabase
            .from('ready_posts')
            .update({
              status: 'ready',
              publication_status: 'pending'
            })
            .eq('id', post.metadata.ready_post_id);
        }

        await supabase.rpc('force_sync_ready_posts');
        await supabase.rpc('auto_publish_ready_posts');

        toast.success('Post queued for retry');
      } else {
        if (post?.id) {
          await supabase
            .from('posts')
            .update({
              status: 'error',
              metadata: {
                ...post.metadata,
                error: 'Post reset by user',
                error_timestamp: new Date().toISOString(),
                is_in_progress: false,
                stage: 'error'
              }
            })
            .eq('id', post.id);
        }

        await supabase
          .from('scheduled_tasks')
          .update({ status: 'active' })
          .eq('id', task.id);

        const { error: createError } = await supabase.rpc('create_direct_post', {
          p_task_id: task.id,
          p_user_id: user.id,
          p_content: null,
          p_image_url: null,
          p_is_test: false
        });

        if (createError) {
          throw createError;
        }

        toast.success('Post has been reset');
      }

      await loadTestHistory();
      await loadReadyPost();
      if (onPostUpdate) onPostUpdate();
    } catch (error) {
      console.error('Error resetting post:', error);
      toast.error('Failed to reset post');
    } finally {
      setIsResetting(false);
    }
  };

  const handlePublishPost = async () => {
    if (!task?.id || isPublishing) return;

    setIsPublishing(true);
    setPublishProgress('Preparing to publish...');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Check if a post is already published for THIS OCCURRENCE
      // Use the ready post's created date, not task.scheduled_for (which may be in the future)
      let checkDate = new Date();

      // If we have a ready post, use its creation date
      if (readyPost) {
        checkDate = new Date(readyPost.created_at);
      } else if (post) {
        checkDate = new Date(post.created_at);
      }

      checkDate.setHours(0, 0, 0, 0);
      const nextDay = new Date(checkDate);
      nextDay.setDate(nextDay.getDate() + 1);

      const { data: existingPosts, error: checkError } = await supabase
        .from('posts')
        .select('*')
        .eq('task_id', task.id)
        .eq('status', 'published')
        .gte('created_at', checkDate.toISOString())
        .lt('created_at', nextDay.toISOString())
        .limit(1);

      if (checkError) throw checkError;

      if (existingPosts && existingPosts.length > 0) {
        throw new Error('A post for this date has already been published');
      }

      setPublishProgress('Publishing post...');

      // Call the manual_publish_post function
      const result = await manualPublishPost(task.id, user.id);

      if (!result) {
        throw new Error('No response received from publishing service');
      }

      if (!result.success) {
        throw new Error(result.error || 'Publishing service returned empty response');
      }

      setPublishProgress('Post published successfully!');
      toast.success('Post published successfully!');

      // Refresh data
      await loadTestHistory();
      await loadReadyPost();
      if (onPostUpdate) onPostUpdate();

    } catch (error) {
      console.error('Error publishing post:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to publish post');
    } finally {
      setIsPublishing(false);
      setPublishProgress(null);
    }
  };

  const handleEditContent = () => {
    if (readyPost) {
      setEditedContent(readyPost.content || '');
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedContent('');
  };

  const handleSaveContent = async () => {
    if (!readyPost?.id || !editedContent.trim()) {
      toast.error('Treść nie może być pusta');
      return;
    }

    setIsSaving(true);
    try {
      const { data, error } = await supabase.rpc('update_ready_post_content', {
        p_ready_post_id: readyPost.id,
        p_new_content: editedContent.trim()
      });

      if (error) throw error;

      if (data?.success) {
        toast.success('Treść zaktualizowana pomyślnie');
        setIsEditing(false);
        await loadReadyPost();
        if (onPostUpdate) onPostUpdate();
      } else {
        throw new Error(data?.error || 'Nie udało się zapisać treści');
      }
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error(error instanceof Error ? error.message : 'Nie udało się zapisać treści');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEmojiSelect = (emoji: string) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const scrollTop = textarea.scrollTop;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = editedContent;
    const before = text.substring(0, start);
    const after = text.substring(end);

    const newText = before + emoji + after;
    setEditedContent(newText);

    setTimeout(() => {
      textarea.scrollTop = scrollTop;
      const newCursorPos = start + emoji.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 0);
  };

  // Get content and image from webhook response if available
  // Handle array response format
  let webhookResponse = post?.metadata?.webhook_response;
  if (Array.isArray(webhookResponse)) {
    webhookResponse = webhookResponse[0];
  }
  
  // Helper: detect default/placeholder FlowAI content from n8n
  const isDefaultFlowAIContent = (content: string | null | undefined): boolean => {
    if (!content) return false;
    const lower = content.toLowerCase();
    return (lower.includes('flowai') && lower.includes('productivity')) ||
           lower.includes('flowai.example.com');
  };
  
  // FIXED PRIORITY ORDER for display content:
  // 1. readyPost.content (validated, ready for publication — highest priority)
  // 2. post.content (from posts table — already processed by cron.php)
  // 3. webhookResponse.data.content (raw webhook — only if not FlowAI default)
  // 4. task.content (original task content — ultimate fallback)
  let displayContent: string | undefined;
  let displayImage: string | undefined;
  
  // Priority 1: Ready post (validated content)
  if (readyPost?.content) {
    displayContent = readyPost.content;
    displayImage = readyPost.image_url;
  }
  
  // Priority 2: Post content (processed by cron.php webhook handler)
  if (!displayContent && post?.content) {
    displayContent = post.content;
    displayImage = displayImage || post.image_url;
  }
  
  // Priority 3: Webhook response (raw — only as fallback, skip FlowAI defaults)
  if (!displayContent && webhookResponse?.data?.content && !isDefaultFlowAIContent(webhookResponse.data.content)) {
    displayContent = webhookResponse.data.content;
  }
  if (!displayImage && webhookResponse?.data?.image_url) {
    displayImage = webhookResponse.data.image_url;
  }
  
  // Priority 4: Post image (if still missing)
  if (!displayImage && post?.image_url) {
    displayImage = post.image_url;
  }
  
  // Priority 5: Task content (ultimate fallback)
  if (!displayContent && task) {
    displayContent = task.content;
  }

  // CRITICAL FIX: Check if post is for this specific task occurrence
  // For recurring tasks, don't show old posts for future occurrences
  const scheduledDateTime = new Date(task?.scheduled_for || new Date());
  const now = new Date();

  // Check if task is scheduled for the future (either future date OR today but later time)
  const isFutureTask = scheduledDateTime > now;

  // For date comparison (ignoring time)
  const taskDate = new Date(task?.scheduled_for || new Date());
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  taskDate.setHours(0, 0, 0, 0);

  // Filter out posts from previous cycles for future task occurrences
  let displayPost: any = null;

  if (post) {
    // Check if this post is for the current task occurrence
    const postDate = new Date(post.published_at || post.created_at);
    postDate.setHours(0, 0, 0, 0);

    // For future tasks, only show posts if they were created on the same day as scheduled
    if (isFutureTask && postDate.getTime() !== taskDate.getTime()) {
      // This is an old post from a previous cycle - don't show it
      displayPost = null;
    } else {
      displayPost = post;
    }
  }

  // If no valid post, try ready_post
  if (!displayPost && readyPost) {
    displayPost = readyPost;
  }

  // Check if the post is published by looking at multiple indicators
  const isPostPublished = displayPost && (
    displayPost.status === 'published' ||
    displayPost.platform_post_id ||
    displayPost.platform_post_url ||
    displayPost.metadata?.platform_post_id ||
    displayPost.metadata?.platform_post_url
  );

  // Check if the post is ready for publication
  const isPostReady = displayPost && (
    displayPost.status === 'ready' ||
    (displayPost.table === 'ready_posts' && displayPost.is_default)
  );

  // If no post to display (future task or no post ready), show a placeholder
  if (!displayPost && task) {
    const timeUntil = scheduledDateTime.getTime() - now.getTime();
    const hoursUntil = Math.floor(timeUntil / (1000 * 60 * 60));
    const minutesUntil = Math.floor((timeUntil % (1000 * 60 * 60)) / (1000 * 60));
    const isPastDue = scheduledDateTime < now;

    let statusMessage = 'Activate the task to schedule publication';
    let bgColorClass = 'bg-blue-50';

    if (task?.status === 'active') {
      if (isPastDue) {
        bgColorClass = 'bg-orange-50';
        if (task.cycle && task.cycle !== 'once') {
          statusMessage = `This recurring task was scheduled for ${format(scheduledDateTime, 'PPP p')}. The post should be generated and published automatically. If it hasn't appeared yet, it may still be processing.`;
        } else {
          statusMessage = `This task was scheduled for ${format(scheduledDateTime, 'PPP p')}. The post should be generated and published automatically. If it hasn't appeared yet, it may still be processing.`;
        }
      } else if (isFutureTask) {
        if (taskDate.getTime() === today.getTime()) {
          statusMessage = `This post will be generated and published today at ${format(scheduledDateTime, 'p')} (in ${hoursUntil}h ${minutesUntil}m)`;
        } else {
          statusMessage = `This post will be generated and published on ${format(taskDate, 'PPP')} at ${format(scheduledDateTime, 'p')}`;
        }
      } else {
        statusMessage = 'Post will be published at the scheduled time';
      }
    }

    return (
      <div className={`flex flex-col items-center justify-center h-full p-6 rounded-lg ${bgColorClass}`}>
        <PostStatus post={null} task={task} isFutureTask={isFutureTask} />
        <p className="mt-4 text-sm text-gray-600 text-center max-w-md">
          {statusMessage}
        </p>
        {/* Show Story slides preview for scheduled Story tasks */}
        {task.content_type === 'story' && task.id && (
          <div className="w-full mt-4">
            <StoryPreviewPanel taskId={task.id} />
          </div>
        )}
      </div>
    );
  }

  // If we have a task but no post, show a placeholder
  if (!displayPost && !task) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 rounded-lg bg-gray-50">
        <p className="text-sm text-gray-500 text-center">
          Select a task to view post details
        </p>
      </div>
    );
  }

  const getPostErrorInfo = () => {
    // Only show error for the LATEST post — if a newer non-error post exists,
    // the old error is from a previous execution and should be ignored
    let errorPost = null;
    if (displayPost?.status === 'error') {
      errorPost = displayPost;
    } else {
      // Check if the latest post is an error
      const sortedPosts = (task?.posts || [])
        .slice()
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      const latestPost = sortedPosts[0];
      if (latestPost?.status === 'error') {
        errorPost = latestPost;
      }
    }

    if (!errorPost) return null;

    const errorMessage = errorPost.metadata?.error ||
      errorPost.metadata?.error_details?.message ||
      'Publication failed';
    const errorTimestamp = errorPost.metadata?.error_timestamp ||
      errorPost.metadata?.first_error_time ||
      errorPost.updated_at;
    const attemptCount = errorPost.publication_attempt_count ||
      errorPost.metadata?.retry_count ||
      1;
    const maxAttempts = 12;
    const retryInterval = 5;
    const lastAttempt = errorPost.last_publish_attempt || errorPost.updated_at;
    const firstErrorTime = errorPost.metadata?.first_error_time || lastAttempt;

    let canRetry = true;
    let actionText = 'System ponowi próbę automatycznie.';
    let actionLink: string | null = null;
    let errorType: 'identity' | 'account_restricted' | 'permission' | 'token' | 'content' | 'media' | 'unknown' = 'unknown';
    const isPermanent = errorPost.metadata?.is_permanent === true;
    const errorCode = errorPost.metadata?.error_code || '';
    const resolutionInstructions = errorPost.metadata?.resolution_instructions || '';

    // Identity verification required
    if (
      errorCode === 'FACEBOOK_IDENTITY_REQUIRED' ||
      errorPost.metadata?.error_type === 'identity_verification' ||
      errorMessage.toLowerCase().includes('tożsamość') ||
      errorMessage.toLowerCase().includes('identity') ||
      errorMessage.toLowerCase().includes('verify your identity')
    ) {
      errorType = 'identity';
      actionText = resolutionInstructions || 'Otwórz aplikację Facebook na telefonie i potwierdź swoją tożsamość dla tej strony.';
      canRetry = false;
    // Account restricted/disabled
    } else if (
      errorCode === 'FACEBOOK_ACCOUNT_RESTRICTED' ||
      errorPost.metadata?.error_type === 'account_restricted' ||
      errorMessage.toLowerCase().includes('restricted') ||
      errorMessage.toLowerCase().includes('disabled') ||
      errorMessage.toLowerCase().includes('zablokowane')
    ) {
      errorType = 'account_restricted';
      actionText = resolutionInstructions || 'Twoje konto lub strona Facebook została ograniczona. Sprawdź ustawienia konta na Facebook.';
      canRetry = false;
    } else if (errorMessage.toLowerCase().includes('permission') || errorCode === 'FACEBOOK_PERMISSION_DENIED') {
      errorType = 'permission';
      actionText = resolutionInstructions || 'Brak wymaganych uprawnień. Połącz ponownie konto Facebook.';
      actionLink = '/accounts';
      canRetry = false;
    } else if (errorMessage.toLowerCase().includes('token') || errorMessage.toLowerCase().includes('oauth') || errorCode === 'FACEBOOK_TOKEN_EXPIRED') {
      errorType = 'token';
      actionText = resolutionInstructions || 'Token dostępu wygasł. Połącz ponownie konto Facebook. 💡 Sprawdź też, czy Facebook nie usunął Cię z zarządzania fanpage – wejdź w fanpage → Ustawienia → Konfiguracja Strony → Dostęp do strony i upewnij się, że widniejesz jako „Osoba z dostępem do Facebooka" z pełnymi uprawnieniami.';
      actionLink = '/accounts';
      canRetry = false;
    } else if (errorMessage.toLowerCase().includes('caption') || errorMessage.toLowerCase().includes('too long')) {
      errorType = 'content';
      actionText = 'Edytuj treść posta, aby spełniała wymagania platformy.';
      canRetry = false;
    } else if (errorMessage.toLowerCase().includes('media') || errorMessage.toLowerCase().includes('2207027')) {
      errorType = 'media';
      actionText = 'Media jest nadal przetwarzane. System ponowi próbę automatycznie.';
    }

    const timeElapsed = firstErrorTime ?
      Math.floor((Date.now() - new Date(firstErrorTime).getTime()) / 60000) : 0;
    const timeRemaining = Math.max(0, 60 - timeElapsed);
    const attemptsRemaining = Math.max(0, maxAttempts - attemptCount);
    const isRetryActive = canRetry && attemptCount < maxAttempts && timeRemaining > 0;

    let nextRetry: Date | null = null;
    if (isRetryActive && lastAttempt) {
      nextRetry = new Date(new Date(lastAttempt).getTime() + retryInterval * 60000);
    }

    return {
      message: errorMessage,
      actionText,
      actionLink,
      canRetry,
      attemptCount,
      maxAttempts,
      attemptsRemaining,
      isRetryActive,
      nextRetry,
      timeRemaining,
      retryInterval,
      errorType,
      isPermanent,
      errorCode,
      resolutionInstructions
    };
  };

  const errorInfo = getPostErrorInfo();

  return (
    <div className="space-y-4">
      {/* Error banner for failed posts */}
      {errorInfo && (
        <div className={`p-4 rounded-lg border ${
          errorInfo.errorType === 'identity' || errorInfo.errorType === 'account_restricted'
            ? 'bg-amber-50 border-amber-300'
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-start gap-3">
            {errorInfo.errorType === 'identity' || errorInfo.errorType === 'account_restricted' ? (
              <ShieldAlert className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <h4 className={`text-sm font-semibold mb-1 ${
                errorInfo.errorType === 'identity' || errorInfo.errorType === 'account_restricted'
                  ? 'text-amber-900'
                  : 'text-red-800'
              }`}>
                {errorInfo.errorType === 'identity'
                  ? '⚠️ Wymagana weryfikacja tożsamości'
                  : errorInfo.errorType === 'account_restricted'
                    ? '🚫 Konto ograniczone'
                    : 'Błąd publikacji'}
              </h4>

              {/* IDENTITY VERIFICATION - Special panel */}
              {errorInfo.errorType === 'identity' && (
                <div className="mt-2 space-y-3">
                  <p className="text-sm text-amber-800">
                    Facebook wymaga potwierdzenia Twojej tożsamości, aby publikować jako ta strona.
                  </p>
                  <div className="p-3 bg-amber-100/60 rounded-lg border border-amber-200 space-y-2">
                    <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">Jak rozwiązać:</p>
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold mt-0.5">1</div>
                      <p className="text-sm text-amber-800">
                        <Smartphone className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                        Otwórz <span className="font-semibold">aplikację Facebook</span> na telefonie
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold mt-0.5">2</div>
                      <p className="text-sm text-amber-800">
                        Przejdź do ustawień strony i <span className="font-semibold">potwierdź swoją tożsamość</span>
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold mt-0.5">3</div>
                      <p className="text-sm text-amber-800">
                        <CheckCircle2 className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                        Po weryfikacji kliknij <span className="font-semibold">"Ponów publikację"</span> poniżej
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* ACCOUNT RESTRICTED */}
              {errorInfo.errorType === 'account_restricted' && (
                <div className="mt-2 space-y-2">
                  <p className="text-sm text-amber-800">
                    {errorInfo.actionText}
                  </p>
                  <p className="text-xs text-amber-700">
                    Po rozwiązaniu problemu na Facebooku kliknij "Ponów publikację".
                  </p>
                </div>
              )}

              {/* OTHER ERRORS */}
              {errorInfo.errorType !== 'identity' && errorInfo.errorType !== 'account_restricted' && (
                <>
                  <p className="text-sm text-red-700 break-words">
                    {errorInfo.message}
                  </p>

                  {errorInfo.isRetryActive && (
                    <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-md">
                      <div className="flex items-center gap-2 mb-2">
                        <RefreshCw className="w-4 h-4 text-orange-600 animate-spin" />
                        <span className="text-sm font-medium text-orange-800">
                          Automatyczne ponawianie aktywne
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs text-orange-700">
                          <span>Próba {errorInfo.attemptCount} z {errorInfo.maxAttempts}</span>
                          <span>Pozostało {errorInfo.attemptsRemaining}</span>
                        </div>
                        <div className="w-full bg-orange-200 rounded-full h-2">
                          <div
                            className="bg-orange-500 h-2 rounded-full transition-all"
                            style={{ width: `${(errorInfo.attemptCount / errorInfo.maxAttempts) * 100}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-orange-600">
                          {errorInfo.nextRetry && (
                            <span>Następna: {format(errorInfo.nextRetry, 'HH:mm')}</span>
                          )}
                          <span>Pozostało {errorInfo.timeRemaining} min</span>
                        </div>
                      </div>
                      <p className="text-xs text-orange-600 mt-2">
                        System ponawia co {errorInfo.retryInterval} min przez max 1 godzinę.
                      </p>
                    </div>
                  )}

                  {!errorInfo.isRetryActive && errorInfo.canRetry && (
                    <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Okno ponawiania wygasło.</span> Osiągnięto limit prób.
                      </p>
                    </div>
                  )}

                  {!errorInfo.canRetry && (
                    <p className="text-sm text-red-600 mt-2">
                      <span className="font-medium">Wymagana akcja:</span> {errorInfo.actionText}
                    </p>
                  )}
                </>
              )}

              <div className="flex flex-wrap gap-2 mt-3">
                {errorInfo.actionLink && (
                  <a
                    href={errorInfo.actionLink}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Połącz ponownie konto
                  </a>
                )}
                <button
                  onClick={handleResetPost}
                  disabled={isResetting}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors disabled:opacity-50 ${
                    errorInfo.errorType === 'identity' || errorInfo.errorType === 'account_restricted'
                      ? 'text-amber-800 bg-amber-200 hover:bg-amber-300'
                      : 'text-red-700 bg-red-100 hover:bg-red-200'
                  }`}
                >
                  <RefreshCw className={`w-4 h-4 ${isResetting ? 'animate-spin' : ''}`} />
                  Ponów publikację
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show info banner for future scheduled tasks */}
      {isFutureTask && !displayPost && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CalendarClock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-900">Scheduled for {taskDate.getTime() === today.getTime() ? 'Today' : format(taskDate, 'PPP')}</h3>
              <p className="text-sm text-blue-700 mt-1">
                This post will be automatically generated and published {taskDate.getTime() === today.getTime() ? 'today' : `on ${format(taskDate, 'PPP')}`} at{' '}
                <span className="font-semibold">{format(scheduledDateTime, 'p')}</span>
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PostStatus
            post={displayPost}
            task={task}
            isPublishing={isPublishing}
            publishProgress={publishProgress}
            isFutureTask={isFutureTask}
          />
          {isStuck && (
            <button
              onClick={handleResetPost}
              disabled={isResetting}
              className="flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${isResetting ? 'animate-spin' : ''}`} />
              <span>Reset Post</span>
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {readyPost && !isPostPublished && (
            <button
              onClick={handleEditContent}
              disabled={isEditing}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              <Edit2 className="w-4 h-4" />
              <span>Edytuj</span>
            </button>
          )}
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <History className="w-4 h-4" />
            <span>Test History</span>
            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
            <PlatformIcon platform={task?.platform} />
          </div>
          <div className="flex-1">
            <p className="font-semibold">{task?.connected_accounts?.name || 'Account Name'}</p>
            <p className="text-sm text-gray-500">
              {displayPost?.published_at ? format(new Date(displayPost.published_at), 'PPP p') : 
               displayPost?.created_at ? format(new Date(displayPost.created_at), 'PPP p') : 
               'Not scheduled yet'}
            </p>
          </div>
          {(displayPost?.platform_post_url || displayPost?.metadata?.platform_post_url) && (
            <a 
              href={displayPost.platform_post_url || displayPost.metadata?.platform_post_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
            >
              <ExternalLink className="w-5 h-5" />
            </a>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-3">
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7B4BFF] focus:border-transparent resize-y"
                placeholder="Wprowadź treść posta..."
              />
              <div className="absolute bottom-2 right-2">
                <EmojiPicker onEmojiSelect={handleEmojiSelect} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveContent}
                disabled={isSaving || !editedContent.trim()}
                className="px-4 py-2 bg-[#7B4BFF] text-white rounded-lg hover:bg-[#6A3FEF] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Zapisywanie...</span>
                  </>
                ) : (
                  <span>Zapisz</span>
                )}
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={isSaving}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
              >
                Anuluj
              </button>
            </div>
          </div>
        ) : (
          <div className="prose max-w-none">
            <FormattedText
              content={displayContent || task?.content}
              className="text-gray-700"
            />
          </div>
        )}

        {/* Video/Story/Image preview */}
        {task?.content_type === 'story' && task?.id ? (
          <StoryPreviewPanel taskId={task.id} />
        ) : task?.video_url ? (
          <ReelVideoPreviewPanel videoUrl={task.video_url} coverImageUrl={task.cover_image_url} />
        ) : (
          <PostImage imageUrl={displayImage || imageUrl} logoUrl={task?.metadata?.logo_url} />
        )}
        <PostMetadata metadata={displayPost?.metadata} />

        {showHistory && (
          <div className="mt-4 space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Test History</h4>
            <PostHistory 
              isLoading={isLoadingHistory}
              history={testHistory}
              onSetDefault={handleSetAsDefault}
            />
          </div>
        )}

        {(displayPost?.platform_post_created_at || displayPost?.metadata?.platform_post_created_at) && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm text-gray-500">
              Published: {format(new Date(displayPost.platform_post_created_at || displayPost.metadata?.platform_post_created_at), 'PPP p')}
            </p>
          </div>
        )}
      </div>

      {/* Add publish button if we have a ready post and it's not already published */}
      {isPostReady && !isPostPublished && (
        <div className="flex justify-end">
          <button
            onClick={handlePublishPost}
            disabled={isPublishing}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPublishing ? (
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>{publishProgress || 'Publishing...'}</span>
              </div>
            ) : (
              'Publish Now'
            )}
          </button>
        </div>
      )}
    </div>
  );
}

// Inline Reel video preview for PostPreview panel
const ReelVideoPreviewPanel: React.FC<{ videoUrl: string; coverImageUrl?: string | null }> = ({ videoUrl, coverImageUrl }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlay = () => {
    setIsPlaying(true);
    setTimeout(() => {
      videoRef.current?.play();
    }, 100);
  };

  return (
    <div className="flex gap-3 mt-3">
      {/* Video */}
      <div className="relative rounded-xl overflow-hidden bg-black shadow-md flex-1" style={{ maxWidth: coverImageUrl ? '50%' : '100%' }}>
        {!isPlaying ? (
          <div className="relative cursor-pointer group" onClick={handlePlay}>
            <video
              src={videoUrl}
              className="w-full aspect-[9/16] max-h-80 object-contain"
              muted
              preload="metadata"
            />
            {/* Play button overlay */}
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
              <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-purple-600 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            </div>
            {/* Badge */}
            <div className="absolute top-2 left-2 bg-purple-600/90 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              ▶ Reel
            </div>
          </div>
        ) : (
          <video
            ref={videoRef}
            src={videoUrl}
            controls
            className="w-full aspect-[9/16] max-h-80 object-contain"
          />
        )}
      </div>

      {/* Cover image */}
      {coverImageUrl && (
        <div className="relative rounded-xl overflow-hidden bg-gray-100 shadow-md" style={{ maxWidth: '50%' }}>
          <img
            src={coverImageUrl}
            alt="Okładka Reel"
            className="w-full aspect-[9/16] max-h-80 object-cover"
          />
          <div className="absolute top-2 left-2 bg-gray-800/70 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
            🖼 Okładka
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Story Preview Panel ────────────────────────────────────────────────
// Loads story_parts from DB and renders them as vertical (9:16) slide cards
// with video/image preview and navigation

const StoryPreviewPanel: React.FC<{ taskId: string }> = ({ taskId }) => {
  const [parts, setParts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const loadParts = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('story_parts')
          .select('*')
          .eq('task_id', taskId)
          .order('part_order', { ascending: true });
        if (!error && data) setParts(data);
      } catch (err) {
        console.error('Error loading story parts:', err);
      } finally {
        setLoading(false);
      }
    };
    if (taskId) loadParts();
  }, [taskId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-5 h-5 border-2 border-pink-400 border-t-transparent rounded-full animate-spin" />
        <span className="ml-2 text-sm text-gray-400">Ładowanie slajdów...</span>
      </div>
    );
  }

  if (parts.length === 0) {
    return null;
  }

  const currentPart = parts[activeSlide];

  return (
    <div className="mt-3 space-y-3">
      {/* Title bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-gradient-to-r from-pink-500 to-orange-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full">
            📖 Story
          </div>
          <span className="text-xs text-gray-500">
            {parts.length} {parts.length === 1 ? 'slajd' : parts.length < 5 ? 'slajdy' : 'slajdów'}
          </span>
        </div>
        <span className="text-xs text-gray-400">
          {activeSlide + 1} / {parts.length}
        </span>
      </div>

      {/* Slide viewer */}
      <div className="relative rounded-xl overflow-hidden bg-black shadow-md" style={{ aspectRatio: '9/16', maxHeight: '28rem' }}>
        {currentPart?.media_url ? (
          currentPart.media_type === 'video' ? (() => {
            const startSec = currentPart.metadata?.start_seconds;
            const endSec = currentPart.metadata?.end_seconds;
            const hasSegment = startSec !== undefined && endSec !== undefined;
            const videoSrc = hasSegment
              ? `${currentPart.media_url}#t=${startSec},${endSec}`
              : currentPart.media_url;
            return (
              <video
                key={`slide-video-${activeSlide}-${startSec}-${endSec}`}
                src={videoSrc}
                controls
                playsInline
                className="w-full h-full object-contain"
              />
            );
          })() : (
            <img
              src={currentPart.media_url}
              alt={`Slajd ${activeSlide + 1}`}
              className="w-full h-full object-contain"
            />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-pink-100 to-orange-100">
            <div className="text-center p-6">
              <div className="text-4xl mb-2">📝</div>
              <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed max-w-52">
                {currentPart?.content || 'Brak treści'}
              </p>
            </div>
          </div>
        )}

        {/* Slide number badge */}
        <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
          {activeSlide + 1} / {parts.length}
        </div>

        {/* Duration badge */}
        {currentPart?.duration_seconds && (
          <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white text-[10px] px-2 py-0.5 rounded-full">
            ⏱ {currentPart.duration_seconds}s
          </div>
        )}
      </div>

      {/* Content below media (if has content and media) */}
      {currentPart?.content && currentPart?.media_url && (
        <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap">
          {currentPart.content}
        </p>
      )}

      {/* Navigation dots + arrows */}
      {parts.length > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => setActiveSlide(Math.max(0, activeSlide - 1))}
            disabled={activeSlide === 0}
            className="p-1.5 rounded-full hover:bg-gray-100 disabled:opacity-30 transition-colors"
          >
            <ChevronUp className="w-4 h-4 -rotate-90" />
          </button>

          <div className="flex items-center gap-1.5">
            {parts.map((_: any, i: number) => (
              <button
                key={i}
                onClick={() => setActiveSlide(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === activeSlide
                    ? 'bg-gradient-to-r from-pink-500 to-orange-500 w-5'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          <button
            onClick={() => setActiveSlide(Math.min(parts.length - 1, activeSlide + 1))}
            disabled={activeSlide === parts.length - 1}
            className="p-1.5 rounded-full hover:bg-gray-100 disabled:opacity-30 transition-colors"
          >
            <ChevronDown className="w-4 h-4 -rotate-90" />
          </button>
        </div>
      )}
    </div>
  );
};